import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { PermissionsGetPermissionsQuery } from 'generated/resources/graphql'
import { xor } from 'lodash'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import ContextlessPermissionsList from 'modules/permissions/components/ContextlessPermissionsList'

import ConfirmationModal from './ConfirmationModal'
import VerbsWithContext from './VerbsWithContext'
import { ToggleablePermission } from './types'

type Permission = PermissionsGetPermissionsQuery['permissions']['permissions'][number]

interface Props {
  disabled: boolean
  permissions: Permission[]
  permissionGroupId: string
}

function Item({ disabled, permissions, permissionGroupId }: Props) {
  const [modifiedPermissionIds, setModifiedPermissionIds] = useState<string[]>([])
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false)

  function buildToggleablePermissions(perms: Permission[]): ToggleablePermission[] {
    return perms.map((permission) => ({
      id: permission.id,
      verb: permission.verb,
      verbLabel: permission.verbLabel,
      verbDescription: permission.verbDescription,
      resource: permission.resource,
      resourceTags: permission.resourceTags,
      category: permission.category,
      context: permission.permissionContext,
      contextLess: permission.contextless,
      toggled: modifiedPermissionIds.includes(permission.id)
        ? !permission.authorized
        : permission.authorized,
      toggle: () => {
        const updatedModifiedPermissionIds = xor(modifiedPermissionIds, [permission.id])
        setModifiedPermissionIds(updatedModifiedPermissionIds)
      },
    }))
  }

  const saveAllFunction = () => {
    setConfirmationModalOpen(true)
  }

  const changes = () =>
    modifiedPermissionIds.map((id) => {
      const permission = permissions.find((p) => p.id === id)
      if (!permission) {
        throw new Error(`Permission with id "${id}" not found`)
      }
      return {
        id,
        verb: permission.verb,
        verbLabel: permission.verbLabel,
        resource: permission.resource ?? null,
        context: permission.permissionContext,
        authorized: !permission.authorized,
        permissionsGroupId: permissionGroupId,
      }
    })

  const toggleablePermissions = buildToggleablePermissions(permissions)

  const contextlessPermissions = toggleablePermissions.filter((p) => p.contextLess)
  const contextfulPermissions = toggleablePermissions.filter((p) => !p.contextLess && !p.resource)
  const resourcePermissions = toggleablePermissions.filter((p) => !p.contextLess && !!p.resource)

  return (
    <Box gap="s24">
      {contextlessPermissions.length > 0 && (
        <ContextlessPermissionsList permissions={contextlessPermissions} disabled={disabled} />
      )}
      {contextfulPermissions.length > 0 && (
        <VerbsWithContext permissions={contextfulPermissions} disabled={disabled} />
      )}
      {resourcePermissions.length > 0 && (
        <VerbsWithContext permissions={resourcePermissions} disabled={disabled} />
      )}
      {modifiedPermissionIds.length > 0 && (
        <Box padding="s16" alignItems="flexEnd">
          <Button onClick={saveAllFunction}>{i18n.t('common.save_changes')}</Button>
        </Box>
      )}
      {confirmationModalOpen && (
        <ConfirmationModal
          onClose={() => {
            setConfirmationModalOpen(false)
            setModifiedPermissionIds([])
          }}
          permissionGroupId={permissionGroupId}
          changes={changes()}
        />
      )}
    </Box>
  )
}

export default observer(Item)
